import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { View } from '../../types';
import { ShelterDashboard } from './ShelterDashboard';
import { LoginView, RegisterView } from '../../components/AuthViews';
import {
    Heart, ShieldAlert, ArrowLeft,
    Zap, CheckCircle2, ArrowRight, Home, MapPin,
    Activity, Mail, LogOut,
    Users, Calendar, ChevronRight, Award, Plus, FileText
} from 'lucide-react';

interface ShelterPortalViewProps {
    onNavigate: (view: View) => void;
}

export const ShelterPortalView: React.FC<ShelterPortalViewProps> = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    const [activePortalTab, setActivePortalTab] = useState<'discover' | 'login' | 'register'>('login');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'login') {
            setActivePortalTab('login');
        } else if (action === 'register' || params.get('role') === 'shelter') {
            setActivePortalTab('register');
        }
    }, []);

    const handleInnerNavigate = (view: View) => {
        if (view === View.REGISTER) {
            setActivePortalTab('register');
        } else if (view === View.LOGIN) {
            setActivePortalTab('login');
        } else if (view === View.LANDING || view === View.SHELTER_DASHBOARD) {
            setActivePortalTab('discover');
        } else {
            onNavigate(view);
        }
    };

    const handleLogout = () => {
        if (confirm("¿Estás seguro de que quieres cerrar sesión del Portal de Albergues?")) {
            logout();
            setActivePortalTab('login');
        }
    };

    // CASE 1: Shelter is Logged In
    if (user && user.role?.includes('shelter')) {
        return (
            <div className="min-h-screen bg-slate-50 w-full animate-in fade-in duration-500">
                <ShelterDashboard onExit={handleLogout} />
            </div>
        );
    }

    // CASE 2: Non-Shelter User is Logged In (Restricted Access Screen)
    if (user && user.role !== 'shelter') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden font-sans">
                <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl p-10 md:p-12 rounded-[3rem] shadow-2xl max-w-xl w-full text-center relative z-10 animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-orange-500/10 border border-orange-500/30 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl shadow-orange-950/20">
                        <ShieldAlert size={40} className="text-orange-500 animate-pulse" />
                    </div>

                    <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4">
                        Acceso Restringido Albergues
                    </h2>
                    
                    <p className="text-slate-400 font-medium text-sm leading-relaxed mb-8 max-w-md mx-auto">
                        Esta sección del ecosistema <span className="text-orange-400 font-bold">Amigo Peludo Albergues</span> está reservada exclusivamente para fundaciones, albergues y centros de rescate autorizados.
                    </p>

                    <div className="bg-slate-950/50 rounded-2xl p-6 mb-8 border border-slate-800 text-left">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Usuario Activo</p>
                        <p className="text-sm font-black text-orange-300 italic uppercase">{user.name}</p>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{user.email}</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => {
                                logout();
                                setActivePortalTab('login');
                            }}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-4 px-6 rounded-2xl font-black uppercase text-xs tracking-widest transition-all active:scale-95 border border-slate-700"
                        >
                            Cambiar de Cuenta
                        </button>
                        <button
                            onClick={() => onNavigate(View.USER_HUB)}
                            className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white py-4 px-6 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-lg shadow-orange-900/30 active:scale-95 flex items-center justify-center gap-2"
                        >
                            Volver al Panel General <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // CASE 3: Guest / Not Logged In
    return (
        <div className="bg-slate-50 min-h-screen overflow-x-hidden font-sans selection:bg-orange-600 selection:text-white pb-20">
            {/* Top Branded Navigation Header */}
            <nav className="fixed top-0 left-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-slate-100 px-6 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => {
                            setActivePortalTab('discover');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="bg-orange-500 p-2.5 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-orange-100">
                            <Home size={20} />
                        </div>
                        <div className="text-left">
                            <span className="font-black text-slate-800 italic uppercase tracking-tighter text-base leading-none block">Amigo Peludo Albergues</span>
                            <span className="text-[8px] font-black text-orange-500 tracking-[0.25em] uppercase block mt-0.5">SHELTER PLATFORM</span>
                        </div>
                    </button>

                    {/* Navigation Actions — visible en todos los tamaños */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={() => setActivePortalTab('discover')}
                            className={`hidden sm:flex px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'discover'
                                    ? 'bg-orange-50 text-orange-600'
                                    : 'text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            Descubrir
                        </button>
                        <button
                            onClick={() => setActivePortalTab('login')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'login'
                                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-200'
                                    : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            Ingresar
                        </button>
                        <button
                            onClick={() => setActivePortalTab('register')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'register'
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-slate-900 text-white hover:bg-orange-500'
                            }`}
                        >
                            <span className="hidden sm:inline">Crear </span>Cuenta
                        </button>
                    </div>
                </div>
            </nav>

            {/* Content Switcher */}
            <div className="pt-20 md:pt-24 max-w-7xl mx-auto px-6 animate-in fade-in duration-300">
                {activePortalTab === 'discover' && (
                    <div className="animate-in fade-in slide-in-from-bottom-6 duration-500">
                        {/* Hero Section */}
                        <section className="relative py-12 md:py-20 overflow-hidden text-center lg:text-left">
                            <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-16">
                                <div className="flex-1">
                                    <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 px-4.5 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest mb-6">
                                        <Zap size={14} className="animate-pulse" /> Ecosistema de Rescate y Bienestar
                                    </div>
                                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-slate-900 leading-[1.1] mb-6 uppercase italic tracking-tighter">
                                        Gestión Profesional para <span className="text-orange-500">Albergues</span> y Fundaciones
                                    </h1>
                                    <p className="text-slate-500 text-base md:text-lg font-semibold leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0">
                                        Amigo Peludo Albergues es la suite más completa para digitalizar tu refugio. Administra historiales médicos de rescatados, gestiona solicitudes de adopción en línea y simplifica la vinculación familiar.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                        <button
                                            onClick={() => setActivePortalTab('register')}
                                            className="bg-orange-500 text-white px-10 py-4.5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-orange-200 hover:bg-slate-900 transition-all flex items-center justify-center gap-2.5"
                                        >
                                            Empezar Ahora <ArrowRight size={16} />
                                        </button>
                                        <button
                                            onClick={() => setActivePortalTab('login')}
                                            className="bg-white border-2 border-slate-200 text-slate-600 px-10 py-4.5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] hover:bg-slate-50 transition-all"
                                        >
                                            Ya tengo cuenta
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 relative w-full max-w-md lg:max-w-none mx-auto">
                                    <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white bg-slate-100">
                                        <img
                                            src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&q=80&w=800"
                                            alt="Shelter Preview"
                                            className="w-full h-auto"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div className="absolute -top-10 -right-10 w-64 h-64 bg-orange-400/10 rounded-full blur-3xl -z-10"></div>
                                    <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl -z-10"></div>
                                </div>
                            </div>
                        </section>

                        {/* Features Showcase */}
                        <section className="bg-white rounded-[3.5rem] py-16 md:py-24 px-6 md:px-12 my-12 border border-slate-200/50 shadow-xl">
                            <div className="text-center mb-16">
                                <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase italic tracking-tighter mb-4">Herramientas Ideadas para Salvaguardar Vidas</h2>
                                <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Tecnología ágil para el voluntariado y rescate</p>
                                <p className="text-slate-500 text-base font-medium leading-relaxed max-w-3xl mx-auto mt-4">
                                    Gestiona el proceso completo de adopción responsable: desde la recepción del animal rescatado hasta la vinculación familiar. Publica perros en adopción y gatos en adopción con fichas digitales detalladas, coordina voluntarios en tránsito y cumple los requisitos de tenencia responsable según la normativa vigente.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                                <div className="flex flex-col gap-6 group">
                                    <div className="rounded-[2rem] overflow-hidden shadow-lg border-4 border-slate-50 relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&q=80&w=800"
                                            alt="Medical Followup"
                                            className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-3.5 flex items-center gap-3">
                                            <Activity className="text-orange-500" /> Carnet de Salud y Ficha de Reinserción
                                        </h3>
                                        <p className="text-slate-500 font-medium text-sm leading-relaxed">
                                            Lleva un control milimétrico del progreso de cuarentena, test virales, desparasitaciones, vacunas y castración. Genera confianza absoluta en los adoptantes proveyendo el historial de salud digital oficial.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-6 group">
                                    <div className="rounded-[2rem] overflow-hidden shadow-lg border-4 border-slate-50 relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&q=80&w=800"
                                            alt="Adoption Flow"
                                            className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-3.5 flex items-center gap-3">
                                            <Users className="text-orange-500" /> Solicitudes de Adopción Digitales
                                        </h3>
                                        <p className="text-slate-500 font-medium text-sm leading-relaxed">
                                            Filtra de manera sencilla y eficiente a los postulantes. Visualiza sus datos socio-ambientales, tipo de hogar y agenda visitas de inspección directamente en el panel sin papeleos redundantes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                {activePortalTab === 'login' && (
                    <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <LoginView 
                            initialMode="SHELTER" 
                            hideRegister={true} 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}

                {activePortalTab === 'register' && (
                    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <RegisterView 
                            role="shelter" 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default ShelterPortalView;
